import { useEffect, useRef, useState } from 'react';
import { ApiError, fetchArticle } from '../api';
import { pillarColor } from '../constants';
import { navLabel } from '../i18n';
import { useSettings } from '../settings';
import type { ArticleDetailDTO } from '../types';
import { dotClass, formatDate, textClass, topRuleClass } from '../lib/region';
import { navigate } from '../lib/router';
import { CoinIcon, EyeIcon, ShieldCheckIcon } from './icons';

type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; article: ArticleDetailDTO }
  | { status: 'error'; message: string; notFound: boolean };

/**
 * Opening a story is what increments the counter, so this effect must run once
 * per article id. No AbortController: StrictMode's mount/unmount/mount cycle
 * would cancel the first request and the ref guard would skip the second,
 * leaving the page stuck on the skeleton.
 */
export default function ArticleView({ id }: { id: string }) {
  const { t, locale } = useSettings();
  const [state, setState] = useState<DetailState>({ status: 'loading' });
  const openedId = useRef<string | null>(null);

  useEffect(() => {
    // A new article counts one view; switching language re-fetches the same
    // article and must not inflate the counter.
    const isNewArticle = openedId.current !== id;
    openedId.current = id;

    fetchArticle(id, { locale, count: isNewArticle })
      .then((article) => setState({ status: 'ready', article }))
      .catch((error: unknown) => {
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Request failed',
          notFound: error instanceof ApiError && error.status === 404,
        });
      });
  }, [id, locale]);

  const back = () => navigate('/');

  if (state.status === 'loading') {
    return (
      <div className="mx-auto w-full max-w-[820px] animate-pulse px-4 py-12 sm:px-6">
        <div className="h-2 w-24 bg-ink/10" />
        <div className="mt-5 h-10 w-full bg-ink/10" />
        <div className="mt-3 h-10 w-3/4 bg-ink/10" />
        <div className="mt-8 space-y-3">
          {[0, 1, 2, 3, 4].map((row) => (
            <div key={row} className="h-3 w-full bg-ink/10" />
          ))}
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="mx-auto w-full max-w-[820px] px-4 py-20 text-center sm:px-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink-dim">
          {state.notFound ? '404' : t('wireUnavailable')}
        </p>
        <h1 className="mt-3 font-serif text-3xl font-bold text-ink">
          {state.notFound ? t('notPublished') : t('wireUnavailable')}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-sm text-ink-muted">
          {state.notFound ? t('notPublishedNote') : state.message}
        </p>
        <button
          type="button"
          onClick={back}
          className="mt-6 border border-rule-strong px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink hover:bg-ink/10"
        >
          {t('backToWire')}
        </button>
      </div>
    );
  }

  const { article } = state;
  const paragraphs = article.content.split(/\n{2,}/).filter(Boolean);

  return (
    <article className="mx-auto w-full max-w-[820px] px-4 pb-16 pt-6 sm:px-6">
      <button
        type="button"
        onClick={back}
        className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim transition-colors hover:text-ink"
      >
        ← {t('backToWire')}
      </button>

      <div className={`mt-6 border-t-4 pt-5 ${topRuleClass(article.agent.region_color)}`}>
        <div className="flex flex-wrap items-center gap-x-2 font-mono text-[9px] uppercase tracking-[0.18em]">
          <span className={textClass(article.agent.region_color)}>{article.agent.name}</span>
          <span className="text-ink-dim" aria-hidden="true">
            /
          </span>
          <span style={{ color: pillarColor(article.pillar) }}>
            {navLabel(locale, article.pillar)}
          </span>
          {article.subsection && (
            <>
              <span className="text-ink-dim" aria-hidden="true">/</span>
              <span className="text-ink-muted">{navLabel(locale, article.subsection)}</span>
            </>
          )}
          {article.machine_translated && (
            <span className="border border-rule px-1 py-px text-[8px] tracking-[0.12em] text-ink-dim">
              {t('autoTranslated')}
            </span>
          )}
        </div>

        <h1 className="mt-3 font-serif text-[2.2rem] font-bold leading-[1.05] tracking-[-0.01em] text-ink sm:text-[3rem]">
          {article.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-rule py-3 font-mono text-[9px] uppercase tracking-[0.14em] text-ink-dim">
          <span className="inline-flex items-center gap-1.5">
            <span
              className={`size-1.5 rounded-full ${dotClass(article.agent.region_color)}`}
              aria-hidden="true"
            />
            {article.agent.reputation_score} rep
          </span>
          <time dateTime={article.created_at} className="normal-case tracking-normal">
            {formatDate(article.created_at)}
          </time>
          <span className="inline-flex items-center gap-1">
            <EyeIcon className="size-3" />
            {article.view_count.toLocaleString()} {t('views')}
          </span>
          <span className="inline-flex items-center gap-1 text-emerald-500">
            <ShieldCheckIcon className="size-3" />
            {t('verified')}
          </span>
          <button
            type="button"
            disabled
            title="Tipping arrives with the wallet flow"
            className="ms-auto inline-flex cursor-not-allowed items-center gap-1 border border-rule px-2 py-1"
          >
            <CoinIcon className="size-3" />
            {t('tip')}
          </button>
        </div>

        <div className="mt-6 space-y-4 font-serif text-[1.05rem] leading-[1.75] text-ink-soft">
          {paragraphs.map((paragraph, index) => (
            <p key={index} className={index === 0 ? 'first-letter:float-start first-letter:me-2 first-letter:font-serif first-letter:text-[3.2rem] first-letter:font-bold first-letter:leading-[0.85]' : ''}>
              {paragraph}
            </p>
          ))}
        </div>

        {article.verification && (
          <section className="mt-10 border border-rule bg-surface-raised p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-rule pb-3">
              <h2 className="inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-500">
                <ShieldCheckIcon className="size-3.5" />
                {t('verification')}
              </h2>
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-dim">
                {t('pipeline')} {article.verification.pipeline_version}
              </span>
            </div>
            <dl className="mt-3 grid gap-3 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-dim sm:grid-cols-2">
              <div>
                <dt>{t('checksPassed')}</dt>
                <dd className="mt-1 text-sm normal-case tracking-normal text-ink">
                  {article.verification.steps_passed} / 4
                </dd>
              </div>
              <div>
                <dt>{t('checked')}</dt>
                <dd className="mt-1 text-sm normal-case tracking-normal text-ink">
                  {formatDate(article.verification.checked_at)}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-xs leading-relaxed text-ink-muted">{t('verificationNote')}</p>
          </section>
        )}
      </div>
    </article>
  );
}
